import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CalendarClock, CheckCircle2, RefreshCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios } from "../api";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return value;
  }
};

const getRenewalBadge = (status) => {
  const styles = {
    active: { label: "Active", color: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
    expiring_soon: { label: "Expiring soon", color: "bg-amber-50 text-amber-700 border border-amber-200" },
    expired: { label: "Expired", color: "bg-red-50 text-red-700 border border-red-200" },
    renewed: { label: "Renewed", color: "bg-sky-50 text-sky-700 border border-sky-200" },
  };
  const entry = styles[status] || { label: String(status || "Active"), color: "bg-slate-100 text-slate-700 border border-slate-200" };
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${entry.color}`}>{entry.label}</span>;
};

export default function AdminAMC() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewingId, setRenewingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminAxios.get("/amc");
      setContracts(data.amc_contracts || []);
    } catch (err) {
      console.error("Failed to load AMC contracts", err);
      toast.error("Failed to load AMC contracts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const summary = useMemo(() => {
    return {
      total: contracts.length,
      active: contracts.filter((c) => c.renewal_status === "active").length,
      expiring_soon: contracts.filter((c) => c.renewal_status === "expiring_soon").length,
      expired: contracts.filter((c) => c.renewal_status === "expired").length,
    };
  }, [contracts]);

  const renewContract = async (bookingId) => {
    setRenewingId(bookingId);
    try {
      const { data } = await adminAxios.post(`/amc/${bookingId}/renew`, { renewal_price: null, additional_notes: "AMC renewed from admin panel" });
      toast.success(`AMC renewed for ${data.booking?.booking_id || bookingId}`);
      await load();
    } catch (err) {
      console.error("Failed to renew AMC", err);
      toast.error("Failed to renew AMC contract");
    } finally {
      setRenewingId(null);
    }
  };

  return (
    <AdminLayout title="AMC Management">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <SummaryCard label="Total AMC" value={summary.total} icon={ShieldCheck} tone="blue" />
          <SummaryCard label="Active" value={summary.active} icon={CheckCircle2} tone="green" />
          <SummaryCard label="Expiring soon" value={summary.expiring_soon} icon={CalendarClock} tone="amber" />
          <SummaryCard label="Expired" value={summary.expired} icon={AlertTriangle} tone="red" />
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h3 className="font-display font-bold text-slate-900">AMC contracts</h3>
              <p className="text-xs text-slate-500 mt-1">Start date, expiry date, and renewal workflow are tracked here.</p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="px-5 py-3">Customer</th>
                  <th className="px-5 py-3">Service</th>
                  <th className="px-5 py-3">Start</th>
                  <th className="px-5 py-3">End</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Renewal</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && contracts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-8 text-center text-slate-400">No AMC contracts found.</td>
                  </tr>
                )}
                {contracts.map((contract) => (
                  <tr key={contract.booking_id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-900">{contract.customer_name || "—"}</div>
                      <div className="text-xs text-slate-500">{contract.booking_id}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-800">{contract.service_name || "—"}</div>
                      <div className="text-xs text-slate-500 capitalize">{contract.category || "—"}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{formatDate(contract.amc_start_date)}</td>
                    <td className="px-5 py-3 text-slate-700">{formatDate(contract.amc_end_date)}</td>
                    <td className="px-5 py-3">{getRenewalBadge(contract.amc_status || "pending")}</td>
                    <td className="px-5 py-3">{getRenewalBadge(contract.renewal_status || "active")}</td>
                    <td className="px-5 py-3 text-right">
                      {(contract.renewal_status === "expiring_soon" || contract.renewal_status === "expired") ? (
                        <button
                          onClick={() => renewContract(contract.booking_id)}
                          disabled={renewingId === contract.booking_id}
                          className="inline-flex items-center gap-2 rounded-lg bg-[#1A3C6E] hover:bg-[#132D53] text-white px-3 py-2 text-xs font-semibold disabled:opacity-60"
                        >
                          <RefreshCcw className="w-3.5 h-3.5" />
                          {renewingId === contract.booking_id ? "Renewing..." : "Renew"}
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">No renewal needed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, icon: Icon, tone }) {
  const tones = {
    blue: "bg-[#1A3C6E]/10 text-[#1A3C6E]",
    green: "bg-[#DCFCE7] text-[#166534]",
    amber: "bg-[#FEF3C7] text-[#B45309]",
    red: "bg-[#FEE2E2] text-[#B91C1C]",
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{label}</div>
          <div className="mt-2 font-display font-bold text-2xl text-slate-900">{value}</div>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tones[tone] || tones.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
